import React from "react";
import Select, { components } from "react-select";

const normalizeValue = (value) =>
  String(value === undefined || value === null ? "" : value).trim();

const getCustomerName = (customer) => {
  const thaiName = normalizeValue(customer && customer.CustomerNameTH);
  const englishName = normalizeValue(customer && customer.CustomerNameEN);

  if (thaiName && englishName && thaiName !== englishName) {
    return `${thaiName} / ${englishName}`;
  }

  return thaiName || englishName || normalizeValue(customer && customer.id);
};

export const buildAdminCustomerOptions = (customers, selectedCustomerIds) => {
  const seenIds = new Set();
  const options = (customers || [])
    .reduce((customerOptions, customer) => {
      const id = normalizeValue(customer && customer.id);
      if (!id || seenIds.has(id)) return customerOptions;

      const name = getCustomerName(customer);
      seenIds.add(id);
      customerOptions.push({
        value: id,
        label: name || id,
        customerId: id,
        customerName: name,
      });
      return customerOptions;
    }, [])
    .sort((first, second) =>
      first.label.localeCompare(second.label, "th", { sensitivity: "base" }),
    );

  (selectedCustomerIds || []).forEach((customerId) => {
    const id = normalizeValue(customerId);
    if (id && !seenIds.has(id)) {
      options.push({
        value: id,
        label: `${id} (saved customer)`,
        customerId: id,
        customerName: "",
        isSavedOnly: true,
      });
    }
  });

  return options;
};

const CustomerOption = (props) => (
  <components.Option {...props}>
    <div className="d-flex align-items-center">
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        tabIndex="-1"
        aria-hidden="true"
        className="mr-2"
        style={{ pointerEvents: "none" }}
      />
      <div>
        <div>{props.data.customerName || props.data.customerId}</div>
        {props.data.customerName &&
          props.data.customerName !== props.data.customerId && (
            <small className="text-muted">
              Customer ID: {props.data.customerId}
            </small>
          )}
        {props.data.isSavedOnly && (
          <small className="text-warning d-block">
            Saved on this account but not found under this node
          </small>
        )}
      </div>
    </div>
  </components.Option>
);

const customerSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderColor: state.isFocused ? "#593bdb" : "#d7dae3",
    boxShadow: state.isFocused ? "0 0 0 1px #593bdb" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#593bdb" : "#b5b9c5",
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#e8f5ee",
    borderRadius: "4px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#176b43",
    fontSize: "13px",
    padding: "5px 4px 5px 8px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#238558",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#dc3545",
      color: "white",
    },
  }),
  menu: (base) => ({ ...base, zIndex: 1070 }),
  option: (base) => ({ ...base, cursor: "pointer" }),
};

const findNode = (nodesList, nodeCode) =>
  (nodesList || []).find(
    (node) => normalizeValue(node && node.nodecode) === nodeCode,
  );

const AdminCustomerAccessSelect = ({
  nodesList,
  selectedNodeCodes,
  value,
  onChange,
  title = "Customer visibility",
  description = "Set access separately for each selected node. This setting is used only for the Staff role.",
  badgeLabel = "Staff only",
}) => {
  const accessDraft = value || {};
  const nodeCodes = selectedNodeCodes || [];

  const setNodeToAllCustomers = (nodeCode) => {
    const nextDraft = { ...accessDraft };
    delete nextDraft[nodeCode];
    onChange(nextDraft);
  };

  const setNodeToSelectedCustomers = (nodeCode, customerIds) => {
    onChange({
      ...accessDraft,
      [nodeCode]: customerIds || [],
    });
  };

  return (
    <div
      className="border rounded p-3"
      style={{ backgroundColor: "#f8f9fb" }}
      data-testid="staff-customer-access"
    >
      <div className="d-flex flex-wrap justify-content-between mb-2">
        <div>
          <h5 className="mb-1">{title}</h5>
          <small className="text-muted">
            {description}
          </small>
        </div>
        <span className="badge badge-info align-self-start mt-1">
          {badgeLabel}
        </span>
      </div>

      {nodeCodes.length === 0 ? (
        <div className="alert alert-light border mb-0 py-2">
          Select at least one node above to configure its customers.
        </div>
      ) : (
        nodeCodes.map((nodeCode) => {
          const node = findNode(nodesList, nodeCode);
          const customers = (node && node.customers) || [];
          const isRestricted = Object.prototype.hasOwnProperty.call(
            accessDraft,
            nodeCode,
          );
          const selectedCustomerIds = isRestricted
            ? accessDraft[nodeCode] || []
            : [];
          const options = buildAdminCustomerOptions(
            customers,
            selectedCustomerIds,
          );
          const selectedOptions = selectedCustomerIds
            .map((id) => options.find((option) => option.value === id))
            .filter(Boolean);
          const nodeName = normalizeValue(node && node.nodename);

          return (
            <div
              className="card mb-2 border"
              key={nodeCode}
              data-testid={`customer-access-node-${nodeCode}`}
            >
              <div className="card-body p-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
                  <div>
                    <strong>{nodeName || nodeCode}</strong>
                    {nodeName && (
                      <span className="badge badge-light border ml-2">
                        {nodeCode}
                      </span>
                    )}
                  </div>
                  <small className="text-muted">
                    {customers.length} customer
                    {customers.length === 1 ? "" : "s"} available
                  </small>
                </div>

                <div className="d-flex flex-wrap mb-2">
                  <div className="custom-control custom-radio mr-4">
                    <input
                      type="radio"
                      className="custom-control-input"
                      id={`customer-access-all-${nodeCode}`}
                      name={`customer-access-${nodeCode}`}
                      checked={!isRestricted}
                      onChange={() => setNodeToAllCustomers(nodeCode)}
                    />
                    <label
                      className="custom-control-label"
                      htmlFor={`customer-access-all-${nodeCode}`}
                    >
                      All customers
                    </label>
                  </div>
                  <div className="custom-control custom-radio">
                    <input
                      type="radio"
                      className="custom-control-input"
                      id={`customer-access-selected-${nodeCode}`}
                      name={`customer-access-${nodeCode}`}
                      checked={isRestricted}
                      onChange={() =>
                        setNodeToSelectedCustomers(nodeCode, selectedCustomerIds)
                      }
                    />
                    <label
                      className="custom-control-label"
                      htmlFor={`customer-access-selected-${nodeCode}`}
                    >
                      Selected customers
                    </label>
                  </div>
                </div>

                {isRestricted && (
                  <div>
                    <Select
                      inputId={`customer-select-${nodeCode}`}
                      aria-label={`Select customers for ${nodeName || nodeCode}`}
                      isMulti
                      isClearable
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      blurInputOnSelect={false}
                      menuPlacement="auto"
                      components={{ Option: CustomerOption }}
                      styles={customerSelectStyles}
                      options={options}
                      value={selectedOptions}
                      placeholder="Search customer name..."
                      noOptionsMessage={({ inputValue }) =>
                        inputValue
                          ? "No matching customers"
                          : "No customers available for this node"
                      }
                      onChange={(selected) =>
                        setNodeToSelectedCustomers(
                          nodeCode,
                          (selected || []).map((option) => option.value),
                        )
                      }
                    />
                    {selectedCustomerIds.length > 0 ? (
                      <small className="text-muted d-block mt-1">
                        {selectedCustomerIds.length} customer
                        {selectedCustomerIds.length === 1 ? "" : "s"} selected.
                        Use × to remove access.
                      </small>
                    ) : (
                      <small className="text-danger d-block mt-1">
                        No customers selected. This account will see no
                        customer records for this node.
                      </small>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      <small className="text-muted d-block mt-2">
        Staff and admin accounts without this setting continue to see all
        customers in their assigned nodes.
      </small>
    </div>
  );
};

export default AdminCustomerAccessSelect;
